import { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Circle } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Filters from "@/components/app/Filters";
import EventSheet from "@/components/app/EventSheet";
import CreateEventSheet from "@/components/app/CreateEventSheet";
import CreateGameSheet from "@/components/app/CreateGameSheet";
import CreateChooser from "@/components/app/CreateChooser";
import GameSheet from "@/components/app/GameSheet";
import BottomNav from "@/components/app/BottomNav";
import MapStoriesStrip from "@/components/app/MapStoriesStrip";
import AdegaSheet from "@/components/app/AdegaSheet";
import NotificationsSheet from "@/components/app/NotificationsSheet";
import { pendingPhoto } from "@/components/app/CameraCapture";
import { isAppBusy } from "@/lib/busy";
import { fullUrl } from "@/components/app/ImageUploader";
import { sportOf } from "@/lib/games";
import { Plus, MapPin, Bell, X } from "lucide-react";
import { toast } from "sonner";

const CENTER = [-22.2139, -49.9456];
const BOUNDS = [[-22.33, -50.05], [-22.10, -49.84]];

const TYPE_EMOJI = {
  churrasco: "🔥", resenha: "🍻", festa: "🎉", atletica: "🏆",
  luau: "🌙", show: "🎸", feira: "🛍️", outro: "📍",
};

const BOTTLE_SVG = '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="#F9C21F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 2h4v4l2 3v12a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9l2-3z"/><path d="M8 13h8"/></svg>';

function makeIcon(ev) {
  const cover = fullUrl(ev.cover_url);
  const ring = ev.has_posts ? '<span class="encosta-pin-ring"></span>' : "";
  const dot = ev.has_posts ? '<span class="encosta-pin-dot"></span>' : "";
  const inner = cover ? `<img src="${cover}" alt="" />` : `<span>${TYPE_EMOJI[ev.type] || "📍"}</span>`;
  const size = cover ? 46 : 36;
  return L.divIcon({
    className: "",
    html: `<div class="encosta-pin ${ev.is_live ? "live" : ""} ${cover ? "has-cover" : ""}">${ring}${inner}${dot}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

function makeAdegaIcon(a) {
  const photo = fullUrl(a.photo_url);
  return L.divIcon({
    className: "",
    html: `<div class="adega-pin ${a.is_open ? "open" : "closed"}">${photo ? `<img src="${photo}" alt="" />` : BOTTLE_SVG}<span class="adega-pin-status"></span></div>`,
    iconSize: [44, 52],
    iconAnchor: [22, 52],
  });
}

function makeGameIcon(g) {
  const label = g.full ? "Completo" : `faltam ${g.remaining}`;
  return L.divIcon({
    className: "",
    html: `<div class="game-pin ${g.full ? "full" : ""} ${g.is_live ? "live" : ""}"><span class="game-pin-icon">${sportOf(g.sport).icon}</span><span class="game-pin-label">${label}</span></div>`,
    iconSize: [84, 62],
    iconAnchor: [42, 62],
  });
}

const clusterIcon = (cluster) => L.divIcon({
  className: "",
  html: `<div class="encosta-cluster">${cluster.getChildCount()}</div>`,
  iconSize: [42, 42],
});

function useMapData(filter) {
  const [events, setEvents] = useState([]);
  const [games, setGames] = useState([]);
  const [adegas, setAdegas] = useState([]);
  const load = async () => {
    if (isAppBusy()) { console.info("[mapa] atualização automática pausada (foto em andamento)"); return; }
    const when = filter.when ? { filter: filter.when } : {};
    try {
      const [ev, gm] = await Promise.all([
        api.get("/events", { params: { ...when, ...(filter.type ? { type: filter.type } : {}) } }),
        api.get("/games", { params: { ...when, ...(filter.sport ? { sport: filter.sport } : {}), ...(filter.level ? { level: filter.level } : {}) } }),
      ]);
      setEvents(ev.data); setGames(gm.data);
    } catch { toast.error("Erro ao carregar o mapa."); }
    api.get("/adegas").then(({ data }) => setAdegas(data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [filter]); // eslint-disable-line
  return { events, games, adegas, load };
}

function MapView() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [filter, setFilter] = useState({ when: null, type: null, sport: null, level: null });
  const [layer, setLayer] = useState("all");
  const { events, games, adegas, load } = useMapData(filter);
  const [map, setMap] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedAdega, setSelectedAdega] = useState(null);
  const [creating, setCreating] = useState(null);
  const [nearby, setNearby] = useState(null);
  const [unread, setUnread] = useState(0);
  const [resumePhoto, setResumePhoto] = useState(null);
  const [notifOpen, setNotifOpen] = useState(false);

  const openRef = (ref) => {
    setNotifOpen(false);
    if (!ref) return;
    if (ref.startsWith("gm_")) setSelectedGame(ref);
    else if (ref.startsWith("ad_")) nav(user?.role === "admin" ? "/admin" : "/adega");
    else openSelected(ref);
  };
  const isAdega = user?.account_type === "adega";

  const openSelected = async (id) => {
    try { const { data } = await api.get(`/events/${id}`); setSelected(data); } catch (_) { toast.error("Esse rolê não está mais disponível."); }
  };

  useEffect(() => {
    const u = new URL(window.location.href);
    const eid = u.searchParams.get("event");
    const aid = u.searchParams.get("adega");
    if (eid) openSelected(eid);
    if (aid) { setLayer("adegas"); setSelectedAdega(aid); }
    if (eid || aid) window.history.replaceState({}, "", u.pathname);
    const pending = pendingPhoto();
    if (pending) {
      console.info("[foto] ação pendente encontrada após recarregar", { event: pending.event_id, hasPhoto: !!pending.dataUrl });
      setResumePhoto(pending);
      openSelected(pending.event_id);
    }
    const next = localStorage.getItem("encosta_next");
    if (next) { localStorage.removeItem("encosta_next"); nav(next); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const aid = selectedAdega && adegas.find((a) => a.id === selectedAdega);
    if (map && aid) map.setView([aid.lat, aid.lng], 16);
  }, [map, selectedAdega, adegas]);

  useEffect(() => {
    if (!user) return;
    api.get("/notifications").then(({ data }) => setUnread(data.filter((n) => !n.read).length)).catch(() => {});
  }, [user, selected, selectedGame, notifOpen]);

  const showNearby = (lat, lng) => {
    setSelected(null);
    setLayer("adegas");
    setNearby({ lat, lng });
    map?.flyTo([lat, lng], 13);
  };

  const openCreate = () => {
    if (!user) { toast.error("Faça login para criar."); return; }
    setCreating("choose");
  };

  const showEvents = layer === "all" || layer === "events";
  const showGames = layer === "all" || layer === "games";
  const showAdegas = layer === "all" || layer === "adegas";

  return (
    <div className="h-[100dvh] w-screen relative overflow-hidden">
      <MapContainer center={CENTER} zoom={13} minZoom={11} maxBounds={BOUNDS} maxBoundsViscosity={1.0} zoomControl={false} className="h-full w-full" ref={setMap}>
        <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" className="osm-dark-tiles" />
        {nearby && <Circle center={[nearby.lat, nearby.lng]} radius={5000} pathOptions={{ color: "#F9C21F", fillOpacity: 0.04, weight: 1.5, dashArray: "6 6" }} />}
        {showEvents && events.filter((ev) => ev.lat == null).map((ev) => (
          <Circle key={`c-${ev.id}`} center={[ev.approx_lat, ev.approx_lng]} radius={500}
            pathOptions={{ color: ev.is_live ? "#ff7bbe" : "#F9C21F", fillColor: ev.is_live ? "#ff7bbe" : "#F9C21F", fillOpacity: 0.1, weight: 1.5 }} />
        ))}
        {showGames && games.filter((g) => g.lat == null).map((g) => (
          <Circle key={`cg-${g.id}`} center={[g.approx_lat, g.approx_lng]} radius={500} pathOptions={{ color: "#22c55e", fillOpacity: 0.08, weight: 1.5 }} />
        ))}
        <MarkerClusterGroup chunkedLoading iconCreateFunction={clusterIcon} showCoverageOnHover={false} maxClusterRadius={45}>
          {showAdegas && adegas.map((a) => (
            <Marker key={`a-${a.id}`} position={[a.lat, a.lng]} icon={makeAdegaIcon(a)} eventHandlers={{ click: () => setSelectedAdega(a.id) }} />
          ))}
          {showGames && games.map((g) => (
            <Marker key={`g-${g.id}`} position={g.lat != null ? [g.lat, g.lng] : [g.approx_lat, g.approx_lng]} icon={makeGameIcon(g)} eventHandlers={{ click: () => setSelectedGame(g.id) }} />
          ))}
          {showEvents && events.map((ev) => (
            <Fragment key={`e-${ev.id}`}>
              <Marker position={ev.lat != null ? [ev.lat, ev.lng] : [ev.approx_lat, ev.approx_lng]} icon={makeIcon(ev)} eventHandlers={{ click: () => openSelected(ev.id) }} />
            </Fragment>
          ))}
        </MarkerClusterGroup>
      </MapContainer>

      <div className="absolute top-0 inset-x-0 glass px-4 pt-safe py-3 z-[500]">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-muted-foreground">Marília, SP</div>
            <div className="text-xl heading font-bold -mt-0.5">Enkosta<span className="text-primary">.</span></div>
          </div>
          <button onClick={() => { if (!user) { toast.error("Entre na sua conta pra ver notificações."); return; } setNotifOpen(true); }} className="relative h-10 w-10 rounded-full glass grid place-items-center press" data-testid="top-notifications" aria-label="Notificações">
            <Bell className="w-5 h-5" />
            {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-secondary text-[10px] font-bold text-black grid place-items-center" data-testid="notifications-badge">{unread > 9 ? "9+" : unread}</span>}
          </button>
        </div>
        <div className="mt-3">
          <Filters value={filter} onChange={setFilter} layer={layer} onLayer={(l) => { setLayer(l); if (l !== "adegas") setNearby(null); }} />
        </div>
        {nearby && (
          <button onClick={() => setNearby(null)} className="mt-2 text-xs flex items-center gap-1.5 text-primary press" data-testid="clear-nearby">
            Adegas até 5 km do rolê <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {showEvents && <MapStoriesStrip events={events} onChanged={load} />}

      {!isAdega && (
        <button onClick={openCreate} className="absolute right-5 bottom-28 z-[500] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-xl grid place-items-center press" data-testid="create-event-fab" aria-label="Criar">
          <Plus className="w-7 h-7" strokeWidth={2.5} />
        </button>
      )}

      {!isAdega && !creating && !selected && !selectedAdega && !selectedGame && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-24 z-[500] glass rounded-full px-4 py-1.5 text-xs flex items-center gap-2 text-muted-foreground whitespace-nowrap">
          <MapPin className="w-3.5 h-3.5 text-primary" /> Toque no + pra criar um rolê ou fechar time
        </div>
      )}

      <BottomNav />

      {selected && (
        <EventSheet eventId={selected.id} initial={selected} onClose={() => { setSelected(null); setResumePhoto(null); load(); }} onChanged={load} onNearbyAdegas={showNearby}
          resumePhoto={resumePhoto?.event_id === selected.id ? resumePhoto : null} />
      )}
      {selectedGame && <GameSheet gameId={selectedGame} onClose={() => { setSelectedGame(null); load(); }} onChanged={load} />}
      {notifOpen && <NotificationsSheet onClose={() => setNotifOpen(false)} onOpenRef={openRef} />}
      {selectedAdega && <AdegaSheet adegaId={selectedAdega} onClose={() => setSelectedAdega(null)} />}
      {creating === "choose" && <CreateChooser onClose={() => setCreating(null)} onPick={setCreating} />}
      {creating === "event" && <CreateEventSheet onClose={() => setCreating(null)} onCreated={() => { setCreating(null); load(); }} />}
      {creating === "game" && <CreateGameSheet onClose={() => setCreating(null)} onCreated={() => { setCreating(null); setLayer("games"); load(); }} />}
    </div>
  );
}

export default MapView;
